import { RecordWithLinks } from "@flatfile/api/api";
import { GetPlansResult } from "../../apis/subscriptionApi";
import moment, { utc } from "moment";
import { DataTableSort, validateEmail } from "@pushpress/shared-components";
import { Badge } from "@pushpress/pushpress-ui";
import {
  FlatfileRecord,
  TPrimitive,
  TRecordDataWithLinks,
} from "@flatfile/hooks";
import {
  getStateAbbreviationToFullName,
  getStateFullNameToAbbreviation,
  EMPTY_DATA,
} from "./constants";
import { joinStrings } from "../../utils/misc";
import {
  MatchState,
  MatchStateType,
  MemberDataType,
  MemberPayloadType,
  PaymentTypes,
  PlanDiscountDataType,
  PlanTypes,
  StepType,
  StripeBillingDataType,
} from "./types";

export function transformMemberDataForPayload(
  data: RecordWithLinks[],
): MemberPayloadType[] {
  return data.map((record) => {
    return {
      addressLineOne: record.values.addressLineOne?.value as string,
      addressLineTwo: record.values.addressLineTwo?.value as string,
      city: record.values.city?.value as string,
      dob: record.values.dob?.value as string,
      email: record.values.email.value as string,
      firstName: record.values.firstName.value as string,
      gender: record.values.gender?.value as string,
      historicalCheckins: (record.values.historicalCheckins?.value as string)
        ? parseInt(record.values.historicalCheckins.value as string)
        : undefined,
      lastName: record.values.lastName.value as string,
      memberSince: record.values.memberSince?.value as string,
      parentEmail: record.values.parentAccount?.value as string,
      phoneNumber: record.values.phoneNumber?.value as string,
      postalCode: record.values.postalCode?.value as string,
      stateOrProvince: record.values.stateOrProvince?.value as string,
    };
  });
}

export function groupPlansByCategory(data?: GetPlansResult["data"] | null) {
  if (!data) {
    return [];
  }
  const recurringPlans = data.filter(
    (plan) => plan.type === PlanTypes.RECURRING,
  );
  const nonRecurringPlans = data.filter(
    (plan) => plan.type === PlanTypes.NON_RECURRING,
  );
  const punchcardPlans = data.filter(
    (plan) => plan.type === PlanTypes.PUNCH_CARD,
  );
  const options = [];
  if (recurringPlans.length) {
    options.push({
      label: `Recurring (${recurringPlans.length})`,
      options: recurringPlans.map((plan) => ({
        value: plan.uuid,
        label: `${plan.name} ($${plan.amount})`,
        category: PlanTypes.RECURRING,
      })),
    });
  }
  if (nonRecurringPlans.length) {
    options.push({
      label: `Non-recurring (${nonRecurringPlans.length})`,
      options: nonRecurringPlans.map((plan) => ({
        value: plan.uuid,
        label: `${plan.name} ($${plan.amount})`,
        category: PlanTypes.NON_RECURRING,
      })),
    });
  }
  if (punchcardPlans.length) {
    options.push({
      label: `Punchcard (${punchcardPlans.length})`,
      options: punchcardPlans.map((plan) => ({
        value: plan.uuid,
        label: `${plan.name} ($${plan.amount})`,
        category: PlanTypes.PUNCH_CARD,
        limitCheckins: plan.limitCheckins,
      })),
    });
  }
  return options;
}

export function getPlanInfo(
  plansData: GetPlansResult["data"],
  planUuid: string,
) {
  return plansData?.find((plan) => plan.uuid === planUuid);
}

export function getPlanOption(
  data: GetPlansResult["data"] | null,
  planUuid: string,
) {
  if (!data) {
    return undefined;
  }
  const plan = data?.find((plan) => plan.uuid === planUuid);
  if (!plan) {
    return undefined;
  }
  return {
    value: plan.uuid,
    label: plan.name,
    category: plan.type,
    limitCheckins: plan.limitCheckins,
  };
}

export function getDiscountOptions(data?: PlanDiscountDataType["data"] | null) {
  if (!data) {
    return [];
  }

  return data.map((discount) => ({
    value: discount.uuid,
    label: discount.name,
  }));
}

export function getDiscountOption(
  data: PlanDiscountDataType["data"] | null,
  discountUuid: string,
) {
  if (!data) {
    return undefined;
  }
  const discount = data?.find((discount) => discount.uuid === discountUuid);
  if (!discount) {
    return undefined;
  }
  return { value: discount?.uuid ?? "", label: discount?.name ?? "" };
}

export function formatDateForPicker(data?: string | null) {
  if (!data) {
    return "";
  }
  // utc needed to avoid timezone issues
  return utc(data).format("YYYY-MM-DD");
}

export function formatDateForPayload(data: string) {
  // Most common date formats
  const formats = [
    "MM/DD/YYYY", // 09/25/2024
    "DD/MM/YYYY", // 25/09/2024
    "YYYY-MM-DD", // 2024-09-25
    "MMM D, YYYY", // Sept 25, 2024
    "MMMM D, YYYY", // September 25, 2024
    "D MMM YYYY", // 25 Sept 2024
    "D-MMM-YYYY", // 25-Sept-2024
    "D/MM/YYYY", // 25/9/2024
    "MM/DD/YY", // 09/25/24
    "D MMM, YYYY", // 25 Sept, 2024
  ];
  const isValidDate = moment(data, formats).isValid();
  if (isValidDate) {
    return moment(data, formats).format("YYYY-MM-DD");
  } else {
    return "Invalid date";
  }
}

export function getPaymentBillingTypeLabel(data: string) {
  switch (data) {
    case PaymentTypes.CASH:
      return "Cash";
    case PaymentTypes.COMP:
      return "Comp";
    case PaymentTypes.STRIPE:
      return "Bank / card";
    default:
      return "Cash";
  }
}

function formatInput(data: string) {
  return data
    .trim()
    .split(" ")
    .map((word) =>
      word.toLowerCase() === "and"
        ? "and"
        : word.toLowerCase() === "d.c."
          ? "D.C."
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function convertToAbbreviationIfValidInput(data: string) {
  const NOT_FOUND = "Not Found";
  if (!data) {
    return NOT_FOUND;
  }

  const formattedInput = formatInput(data);
  const hasValidFullName =
    getStateFullNameToAbbreviation()[
      formattedInput as keyof typeof getStateFullNameToAbbreviation
    ];
  const hasValidAbbreviation =
    getStateAbbreviationToFullName()[
      formattedInput.toUpperCase() as keyof typeof getStateAbbreviationToFullName
    ];

  if (hasValidFullName) {
    return hasValidFullName;
  }
  if (hasValidAbbreviation) {
    return formattedInput.toUpperCase();
  }
  return NOT_FOUND;
}

export function validSubaccounts(data: MemberPayloadType[]) {
  const subaccounts = data.filter((member) => member.parentEmail);
  const result = subaccounts.some((sub) =>
    subaccounts.some((member) => member.email === sub.parentEmail),
  );
  return !result;
}

export function flatFileRecordModifiers(
  record: FlatfileRecord<TRecordDataWithLinks<TPrimitive>>,
) {
  const firstName = record.get("firstName")?.toString();
  const lastName = record.get("lastName")?.toString();
  const email = record.get("email")?.toString();
  const parentAccount = record.get("parentAccount")?.toString();
  const dob = record.get("dob")?.toString();
  const memberSince = record.get("memberSince")?.toString();
  const addressLineOne = record.get("addressLineOne")?.toString();
  const addressLineTwo = record.get("addressLineTwo")?.toString();
  const city = record.get("city")?.toString();
  const stateOrProvince = record.get("stateOrProvince")?.toString();
  const postalCode = record.get("postalCode")?.toString();

  if (postalCode && postalCode.length > 11) {
    record.addError("postalCode", "11 characters or fewer");
  }

  if (firstName) {
    record.set("firstName", firstName.trim());
  }

  if (lastName) {
    record.set("lastName", lastName.trim());
  }

  if (email) {
    record.set("email", email.toLowerCase());
  }

  const isValidEmail = validateEmail(record.get("email")?.toString());

  if (email && !isValidEmail) {
    record.addError("email", "Invalid email address");
  }

  if (email && parentAccount && email === parentAccount) {
    record.addError("parentAccount", "Cannot be your own email");
  }

  if (!parentAccount && !email) {
    record.addError("email", "Email is required");
  }

  if (parentAccount && !email) {
    record.addInfo("email", "Email is preferred");
  }

  if (dob) {
    const formattedDob = formatDateForPayload(dob);
    if (formattedDob === "Invalid date") {
      record.addError("dob", "Invalid date");
    } else {
      record.set("dob", formattedDob);
    }
  }

  if (memberSince) {
    const formattedMemberSince = formatDateForPayload(memberSince);
    if (formattedMemberSince === "Invalid date") {
      record.addError("memberSince", "Invalid date");
    } else {
      record.set("memberSince", formattedMemberSince);
    }
  }

  if (firstName?.length > 45) {
    record.addError("firstName", "45 characters or fewer");
  }

  if (lastName?.length > 45) {
    record.addError("lastName", "45 characters or fewer");
  }

  if (addressLineOne?.length > 45) {
    record.addError("addressLineOne", "45 characters or fewer");
  }

  if (addressLineTwo?.length > 45) {
    record.addError("addressLineTwo", "45 characters or fewer");
  }

  if (city?.length > 45) {
    record.addError("city", "45 characters or fewer");
  }

  if (stateOrProvince) {
    // DB only accepts 2 chars
    const result = convertToAbbreviationIfValidInput(stateOrProvince);
    if (result === "Not Found") {
      record.addError(
        "stateOrProvince",
        "Invalid entry. Please abbreviate (e.g. AZ for Arizona)",
      );
    } else {
      record.set("stateOrProvince", result);
    }
  }
  return record;
}

export function getMemberPayment(member: MemberDataType) {
  const brand = member.billingBrand || "";
  const last4 = member.billingLastFour || "";
  const name = `${brand.slice(0, 1).toUpperCase()}${brand.slice(1).toLowerCase()}`;
  if (!brand || !last4) return "";
  return `${name} •••• ${last4}`;
}

export const getStatusBadge = (status: MatchStateType) => {
  switch (status) {
    case MatchState.UNMATCHED:
      return <Badge variant="warning"> Unmatched </Badge>;
    case MatchState.CONFIRMED:
      return <Badge variant="success"> Confirmed </Badge>;
    case MatchState.REJECTED:
      return <Badge variant="error"> Rejected </Badge>;
    case MatchState.PENDING:
      return <Badge variant="info"> Pending </Badge>;
    default:
      return null;
  }
};

/**
 * Returns true if the member should be shown based on the keyword
 * If the keyword has empty spaces, all parts must be present in the member's full name
 */
export function shouldShowMember(member: MemberDataType, keyword: string) {
  if (!keyword) {
    return true;
  }
  const searchValue = keyword.toLowerCase();
  const fullName = joinStrings(member.firstName, member.lastName).toLowerCase();
  const parts = searchValue.split(" ");
  return parts.every((part) => fullName.includes(part));
}

function getRelatedAccounts(
  membersData: MemberDataType[],
  member: MemberDataType,
) {
  return membersData.filter((m) => {
    if (!member.parentEmail) {
      // For primary accounts, find only sub-accounts
      return m.parentEmail === member.email;
    }
    // For sub-accounts, find parent and siblings
    return (
      m.email === member.parentEmail ||
      (m.parentEmail === member.parentEmail && m.email !== member.email)
    );
  });
}

export function getMembersReadyForActivation(
  membersData: MemberDataType[] | undefined,
) {
  if (!Array.isArray(membersData)) {
    return [];
  }

  return membersData.filter((member) => {
    // Check if member is not already processed
    const isNotProcessed = !member.migrated && !member.migrating;
    const relatedAccounts = getRelatedAccounts(membersData, member);
    // Check if member or any related account is missing a required plan
    const isMissingRequiredPlan =
      (member.needPlan && !member.planUuid) ||
      relatedAccounts.some((account) => account.needPlan && !account.planUuid);

    return isNotProcessed && !isMissingRequiredPlan;
  });
}

export type MemberSortType = "name" | "plan";

export function sortMembers(
  members: MemberDataType[],
  sort: DataTableSort<MemberSortType>,
  plans: GetPlansResult["data"],
) {
  const comparator = createMemberComparator(sort, plans);
  const primaryAccounts = members
    .filter((m) => !m.parentEmail)
    .sort(comparator);
  const subAccounts = members.filter((m) => m.parentEmail).sort(comparator);
  const result = [...primaryAccounts];
  subAccounts.reverse().forEach((subAccount) => {
    const index = result.findIndex((m) => m.email === subAccount.parentEmail);
    result.splice(index + 1, 0, subAccount);
  });
  return result;
}

function createMemberComparator(
  sort: DataTableSort<MemberSortType>,
  plans: GetPlansResult["data"],
) {
  const plansMap = plans.reduce<Record<string, string>>((acc, plan) => {
    acc[plan.uuid] = plan.name;
    return acc;
  }, {});

  if (sort.key === "name") {
    if (sort.order === "asc") {
      return (a: MemberDataType, b: MemberDataType) => {
        const fullNameA = joinStrings(a.firstName, a.lastName);
        const fullNameB = joinStrings(b.firstName, b.lastName);
        return fullNameA?.localeCompare(fullNameB);
      };
    } else {
      return (a: MemberDataType, b: MemberDataType) => {
        const fullNameA = joinStrings(a.firstName, a.lastName);
        const fullNameB = joinStrings(b.firstName, b.lastName);
        return fullNameB?.localeCompare(fullNameA);
      };
    }
  } else if (sort.key === "plan") {
    if (sort.order === "asc") {
      return (a: MemberDataType, b: MemberDataType) => {
        const planA = plansMap[a.planUuid];
        const planB = plansMap[b.planUuid];
        return planA?.localeCompare(planB) ?? 0;
      };
    } else {
      return (a: MemberDataType, b: MemberDataType) => {
        const planA = plansMap[a.planUuid];
        const planB = plansMap[b.planUuid];
        return planB?.localeCompare(planA) ?? 0;
      };
    }
  }
  return () => 0;
}

export const warningDescription =
  'This client will be ready to activate after all parent / sub-accounts in this group have been assigned a plan, or marked "plan not needed".';

export function getMemberStatus(
  member: MemberDataType,
  membersData: MemberDataType[],
): { level: "success" | "warning"; description: string } | null {
  const isReadyToActivate = !member.needPlan || !!member.planUuid;

  // check if parent has a plan
  if (isReadyToActivate && member.parentEmail) {
    const parent = membersData.find((m) => m.email === member.parentEmail);
    if (!parent?.planUuid && parent?.needPlan) {
      return {
        level: "warning",
        description: warningDescription,
      };
    }
  }

  // check if any sub-accounts have no plan
  if (isReadyToActivate) {
    const subAccounts = membersData.filter(
      (m) => m.parentEmail === member.email,
    );
    if (subAccounts.some((m) => !m.planUuid && m?.needPlan)) {
      return {
        level: "warning",
        description: warningDescription,
      };
    }
  }

  // check if sibling sub-accounts have no plan
  if (isReadyToActivate && member.parentEmail) {
    const siblings = membersData.filter(
      (m) => m.parentEmail === member?.parentEmail && m.email !== member.email,
    );
    if (siblings.some((m) => !m.planUuid && m?.needPlan)) {
      return {
        level: "warning",
        description: warningDescription,
      };
    }
  }

  // if all checks pass, member is ready to activate
  if (isReadyToActivate) {
    return {
      level: "success",
      description: "This client is ready to be activated",
    };
  }

  return null;
}

export const activationSteps = (): StepType[] => [
  {
    id: "who-to-activate",
    step: 1,
    title: "Who do you want to activate?",
    isMainStep: true,
  },
  {
    id: "select-members-table",
    step: 1,
    title: "Who do you want to activate?",
    isMainStep: false,
  },
  {
    id: "who-to-prompt",
    step: 2,
    title: "Who should be prompted to add a payment method?",
    isMainStep: true,
  },
  {
    id: "select-payment-collection-table",
    step: 2,
    title: "Who should be prompted to add a payment method?",
    isMainStep: false,
  },
  {
    id: "review-and-activate",
    step: 3,
    title: "Review & activate",
    isMainStep: true,
  },
];

export const isInvalidStripeBillingData = (
  stripeBillingData: StripeBillingDataType["data"],
  memberId?: string,
) => typeof stripeBillingData === "string" || !memberId;

export const hasMultiplePaymentMethods = (
  stripeBillingData: StripeBillingDataType["data"],
  member: MemberDataType,
) => {
  if (!member.stripeId || typeof stripeBillingData === "string") return false;
  return stripeBillingData[member.stripeId]?.paymentMethods.data.length > 1;
};

export const getBillingDataForMember = (
  stripeBillingData: StripeBillingDataType["data"],
  member: MemberDataType,
) => {
  if (isInvalidStripeBillingData(stripeBillingData, member.stripeId)) {
    return EMPTY_DATA;
  }

  const paymentMethods =
    stripeBillingData[member.stripeId!]?.paymentMethods.data;

  if (!paymentMethods || paymentMethods.length === 0) {
    return EMPTY_DATA;
  }

  return paymentMethods.map((paymentMethod) => {
    if (paymentMethod.card) {
      const cardBrand = paymentMethod.card.brand;
      const brandName = `${cardBrand.slice(0, 1).toUpperCase()}${cardBrand
        .slice(1)
        .toLowerCase()} `;
      return (
        <div key={paymentMethod.id}>
          {brandName} •••• {paymentMethod.card.last4}
        </div>
      );
    }

    if (paymentMethod.us_bank_account) {
      const bankName = paymentMethod.us_bank_account.bank_name;
      const brandName = `${bankName.slice(0, 1).toUpperCase()}${bankName
        .slice(1)
        .toLowerCase()} `;
      return (
        <div key={paymentMethod.id}>
          {brandName} •••• {paymentMethod.us_bank_account.last4}
        </div>
      );
    }
    return EMPTY_DATA;
  });
};
